import { Box, Button, Card, CardActionArea, Checkbox, styled, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import React from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckboxBg from "../../Misc/CheckboxBg";
import { To, useNavigate } from "react-router";

export interface SquareCardProps {
  id: number;
  title: string;
  subtitle: string;
  selected: boolean;
  selectMode?: boolean;
  selectOnClick?: boolean;
  maskEdit?: boolean;
  maskDelete?: boolean;
  onCheckboxClick: (id: number) => void;
  onDeleteClick: (id: number) => void;
  onEditClick: (id: number) => void;
  onClick?: (id: number) => void;
  to: To;
}

const SquareCard: React.FC<SquareCardProps> = ({
  id,
  title,
  subtitle,
  selected,
  selectMode = false,
  selectOnClick,
  maskEdit,
  maskDelete,
  to = "",
  onCheckboxClick,
  onDeleteClick,
  onEditClick,
  onClick,
}) => {
  const navigate = useNavigate();

  const handleClick = (e: any, target: "checkbox" | "delete" | "edit") => {
    e.stopPropagation();
    switch (target) {
      case "checkbox":
        onCheckboxClick(id);
        break;
      case "delete":
        onDeleteClick(id);
        break;
      case "edit":
        onEditClick(id);
        break;
      default:
        console.log("unknown target");
    }
  };

  const handleCardClick = () => {
    if (!selectOnClick) {
      if (onClick) onClick(id);
      else return navigate(to);
    }
    onCheckboxClick(id);
  };

  return (
    <StyledCard selected={selected} variant="outlined" aria-selected={selected}>
      <CardActionArea component="div" onClick={handleCardClick}>
        <Stack sx={{ height: "100%" }} justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" position="relative">
            <CardActions className="pack-card-actions" direction="row">
              {!maskEdit && (
                <Button sx={{ mr: 0.5 }} variant="icon" size="small" onClick={(e) => e.stopPropagation()}>
                  <EditOutlinedIcon />
                </Button>
              )}
              {!maskDelete && (
                <Button variant="icon" size="small" onClick={(e) => handleClick(e, "delete")}>
                  <DeleteOutlinedIcon color="error" />
                </Button>
              )}
            </CardActions>
            {selectMode && (
              <>
                <CheckboxBg checked={selected} className="MuiCheckbox-Bg" sx={{ top: 9, right: 9 }} />
                <Checkbox checked={selected} onClick={(e) => handleClick(e, "checkbox")} />
              </>
            )}
          </Stack>
          <Box sx={{ p: 1 }}>
            <Typography mb={1} variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
            <Typography fontWeight={500} color="primary.main">
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </StyledCard>
  );
};

export default SquareCard;

const StyledCard = styled(Card, { shouldForwardProp: (propName) => propName !== "selected" })<{ selected: boolean }>(
  ({ theme, selected }) => ({
    height: "180px",
    borderRightColor: "transparent",
    borderLeftColor: "transparent",
    borderBottomColor: "transparent",
    transition: theme.transitions.create(["border-color", "background"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeOut,
    }),
    ".MuiCardActionArea-root": {
      padding: theme.spacing(1.5),
      height: "100%",
      background: theme.palette.backgroundAlpha.main,

      "&:hover": {
        background: theme.palette.backgroundAlpha.hover,
        ...(selected && {
          background: theme.palette.primary.alpha,
        }),
        ".MuiCheckbox-Bg": {
          opacity: 0.5,
        },
        ".pack-card-actions": {
          opacity: 1,
        },
      },
    },
  })
);

const CardActions = styled(Stack)(({ theme }) => ({
  opacity: 0,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeOut,
  }),
}));
